import { useState, useEffect, useRef } from 'react'
import { message } from 'antd'
import { handleError } from '../../../../lib/utils'

/**
 * Custom hook for managing WebSocket connections
 * @param {string} endpoint - WebSocket endpoint to connect to
 * @param {string} bookingRef - Booking reference to filter messages
 * @param {string} userRef - Current user reference to check against
 * @param {Function} onMessage - Callback when receiving a relevant message
 */
export const useWebSocket = (endpoint, bookingRef, userRef, onMessage) => {
  const [connected, setConnected] = useState(false)
  const socketRef = useRef(null)

  useEffect(() => {
    // Skip if no endpoint provided
    if (!endpoint || !bookingRef) return

    // Track mount state
    let isMounted = true

    try {
      const wsUrl = `${process.env.REACT_APP_NODE_RED_WS_URL}/${endpoint}`
      const socket = new WebSocket(wsUrl)
      socketRef.current = socket

      socket.onopen = () => {
        if (isMounted) {
          setConnected(true)
        }
      }

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          // Only process messages for this booking
          if (data.bookingRef === bookingRef) {
            // Check if another user made the change
            if (data.userRef !== userRef) {
              if (onMessage && isMounted) {
                onMessage(data)
              } else if (isMounted) {
                message.info('Records have been updated by another user. Please refresh')
              }
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      socket.onerror = (error) => {
        console.error('WebSocket connection error', error)
      }

      return () => {
        // Mark as unmounted
        isMounted = false

        // Close socket on cleanup
        if (socket && socket.readyState !== WebSocket.CLOSED) {
          socket.close()
        }

        // Clear references
        socketRef.current = null
      }
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err)
      return () => {
        isMounted = false
      }
    }
  }, [endpoint, bookingRef, userRef]) // Removed onMessage from dependency array to prevent re-renders

  return {
    connected,
    socket: socketRef.current
  }
}
